// src/scenes/GestionStock/StockList.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Menu,
  MenuItem,
  Typography,
  Card,
  CardContent,
  
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Search,
  FilterList,
  Edit,
  Warning,
  Inventory,
  Refresh,
  SwapHoriz,
  History,
  Store as StoreIcon,
  Business as BusinessIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { inventoryService } from '../../services/inventory';
import { succursaleService } from '../../services/inventory'; // ⚠️ NOUVEAU service
import AjusterStockModal from './AjusterStockModal';
import TransfertStockModal from './TransfertStockModal';
import MouvementsStockModal from './MouvementsStockModal';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StockList = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [filters, setFilters] = useState({
    succursale: '',
  });
  
  // Gestion des succursales
  const [succursales, setSuccursales] = useState([]);
  const [loadingSuccursales, setLoadingSuccursales] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  
  // Modals
  const [ajustementModalOpen, setAjustementModalOpen] = useState(false);
  const [transfertModalOpen, setTransfertModalOpen] = useState(false);
  const [mouvementsModalOpen, setMouvementsModalOpen] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total_produits: 0,
    stock_faible: 0,
    rupture: 0,
    total_succursales: 0,
    succursales_actives: 0
  });

  // ========== CHARGEMENT DES DONNÉES ==========
  useEffect(() => {
    fetchStocks();
    fetchSuccursales();
    fetchStats();
  }, [page, rowsPerPage, searchTerm, filters]);

  // Récupération des stocks
  const fetchStocks = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        search: searchTerm || undefined
      };
      
      if (filters.succursale) {
        params.succursale = filters.succursale;
      }

      console.log('🔍 Fetching stocks with params:', params);
      const response = await inventoryService.getStocks(params);
      console.log('✅ Stocks response:', response.data);
      
      if (response.data.results) {
        setStocks(response.data.results);
        setTotalCount(response.data.count);
        
        // Mettre à jour le total des produits dans les stats
        setStats(prev => ({ ...prev, total_produits: response.data.count || 0 }));
      } else if (Array.isArray(response.data)) {
        setStocks(response.data);
        setTotalCount(response.data.length);
        setStats(prev => ({ ...prev, total_produits: response.data.length }));
      } else {
        setStocks([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('❌ Erreur fetchStocks:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des stocks');
      setStocks([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Récupération des succursales (CORRIGÉ - utilise le bon endpoint)
  const fetchSuccursales = async () => {
    setLoadingSuccursales(true);
    try {
      console.log('🔍 Fetching succursales...');
      const response = await succursaleService.getSuccursales(); // ⚠️ ICI le changement
      console.log('✅ Succursales response:', response.data);
      
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      } else if (response.data?.data) {
        data = response.data.data;
      }
      
      setSuccursales(data);
      
      // Mettre à jour les stats des succursales
      const actives = data.filter(s => s.is_active).length;
      setStats(prev => ({
        ...prev,
        total_succursales: data.length,
        succursales_actives: actives
      }));
      
    } catch (err) {
      console.error('❌ Erreur chargement succursales:', err);
    } finally {
      setLoadingSuccursales(false);
    }
  };

  // Récupération des statistiques
  const fetchStats = async () => {
    try {
      console.log('🔍 Fetching stats...');
      const [ruptureRes, faibleRes] = await Promise.all([
        inventoryService.getStocksRupture(),
        inventoryService.getStocksFaible()
      ]);

      setStats(prev => ({
        ...prev,
        rupture: ruptureRes.data.count || ruptureRes.data.length || 0,
        stock_faible: faibleRes.data.count || faibleRes.data.length || 0
      }));
      
      console.log('✅ Stats:', {
        rupture: ruptureRes.data.count || ruptureRes.data.length,
        faible: faibleRes.data.count || faibleRes.data.length
      });
    } catch (err) {
      console.error('❌ Erreur chargement stats:', err);
    }
  };

  // ========== GESTION DES ÉVÉNEMENTS ==========
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
    handleFilterClose();
  };

  const handleFilterReset = () => {
    setFilters({ succursale: '' });
    setPage(0);
    handleFilterClose();
  };

  const handleAjusterStock = (stock) => {
    const succursaleDetails = succursales.find(s => s.id === stock.succursale);
    setSelectedStock({ ...stock, succursale_details: succursaleDetails });
    setAjustementModalOpen(true);
  };

  const handleTransfertStock = (stock) => {
    const succursaleDetails = succursales.find(s => s.id === stock.succursale);
    setSelectedStock({ ...stock, succursale_details: succursaleDetails });
    setTransfertModalOpen(true);
  };

  const handleVoirMouvements = (stock) => {
    const succursaleDetails = succursales.find(s => s.id === stock.succursale);
    setSelectedStock({ ...stock, succursale_details: succursaleDetails });
    setMouvementsModalOpen(true);
  };

  const handleAjustementSuccess = () => {
    fetchStocks();
    fetchStats();
  };

  const handleTransfertSuccess = () => {
    fetchStocks();
    fetchStats();
  };

  // ========== UTILITAIRES ==========
  const getStockStatusChip = (stock) => {
    if (stock.est_en_rupture) {
      return <Chip icon={<Warning />} label="Rupture" color="error" size="small" />;
    }
    if (stock.est_stock_faible) {
      return <Chip icon={<Warning />} label="Stock faible" color="warning" size="small" />;
    }
    return <Chip icon={<Inventory />} label="Normal" color="success" size="small" />;
  };

const formatPrix = (stock) => {
  // Priorité 1 : prix_vente_effectif (si présent et > 0)
  const prixEffectif = parseFloat(stock?.prix_vente_effectif);
  if (prixEffectif > 0) {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2
    }).format(prixEffectif) + ' HTG';
  }

  // Priorité 2 : conditionnements_effectifs → unite → prix_vente
  const conds = stock?.conditionnements_effectifs;
  if (conds && typeof conds === 'object') {
    const prixVente = parseFloat(conds['unite']?.prix_vente);
    if (prixVente > 0) {
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2
      }).format(prixVente) + ' HTG';
    }
  }

  return '—';
};

  const getSelectedSuccursaleName = () => {
    if (!filters.succursale) return 'Filtres';
    const succ = succursales.find(s => s.id === parseInt(filters.succursale));
    return succ ? succ.nom : 'Filtré';
  };

  // ========== RENDU ==========
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestion des Stocks par Succursale
      </Typography>

      {/* ===== CARTES DE STATISTIQUES ===== */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Produits
                  </Typography>
                  <Typography variant="h4">
                    {stats.total_produits}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 30, height: 30 }}>
                  <Inventory />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Stock Faible
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.stock_faible}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 30, height: 30 }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Rupture de Stock
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.rupture}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main', width: 30, height: 30 }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Succursales Actives
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {stats.succursales_actives}/{stats.total_succursales}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 30, height: 30 }}>
                  <StoreIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== BARRE D'OUTILS ===== */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Rechercher produit ou succursale..."
          value={searchTerm}
          onChange={handleSearch}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        
        <Button
          variant={filters.succursale ? "contained" : "outlined"}
          startIcon={<FilterList />}
          onClick={handleFilterClick}
          color={filters.succursale ? "primary" : "inherit"}
          sx={{ minWidth: 150 }}
        >
          {getSelectedSuccursaleName()}
        </Button>
        
        <Tooltip title="Rafraîchir">
          <IconButton onClick={() => { 
            fetchStocks(); 
            fetchSuccursales(); 
            fetchStats(); 
          }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ===== MENU DE FILTRES ===== */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: { maxHeight: 400, width: 300 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Filtrer par succursale
          </Typography>
          
          {loadingSuccursales ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <MenuItem 
                onClick={() => handleFilterChange('succursale', '')}
                selected={!filters.succursale}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemIcon>
                  <StoreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Toutes les succursales" />
                {!filters.succursale && <CheckIcon fontSize="small" color="primary" />}
              </MenuItem>
              
              <Divider sx={{ my: 1 }} />
              
              {succursales
                .filter(s => s.is_active)
                .map((succursale) => (
                  <MenuItem
                    key={succursale.id}
                    onClick={() => handleFilterChange('succursale', succursale.id.toString())}
                    selected={filters.succursale === succursale.id.toString()}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemIcon>
                      {succursale.is_main_branch ? (
                        <BusinessIcon fontSize="small" color="warning" />
                      ) : (
                        <StoreIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={succursale.nom}
                      secondary={succursale.code}
                    />
                    {filters.succursale === succursale.id.toString() && (
                      <CheckIcon fontSize="small" color="primary" />
                    )}
                  </MenuItem>
                ))}
              
              <Divider sx={{ my: 1 }} />
              
              <Button
                fullWidth
                variant="outlined"
                onClick={handleFilterReset}
                size="small"
              >
                Réinitialiser
              </Button>
            </>
          )}
        </Box>
      </Menu>

      {/* ===== TABLEAU DES STOCKS ===== */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Produit</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Succursale</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Stock actuel</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Stock minimum</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Prix vente</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Chargement des stocks...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Alert severity="error">{error}</Alert>
                </TableCell>
              </TableRow>
            ) : stocks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Inventory sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="textSecondary">
                    Aucun stock trouvé
                  </Typography>
                  {filters.succursale && (
                    <Button 
                      variant="text" 
                      onClick={handleFilterReset}
                      sx={{ mt: 1 }}
                    >
                      Voir toutes les succursales
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              stocks.map((stock) => (
                <StyledTableRow key={stock.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {stock.produit_nom}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {stock.produit_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {(() => {
                        const succ = succursales.find(s => s.id === stock.succursale);
                        return succ?.is_main_branch ? (
                          <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                        ) : (
                          <StoreIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
                        );
                      })()}
                      <Box>
                        <Typography variant="body2">
                          {stock.succursale_nom}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {stock.succursale_code}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color={
                        stock.est_en_rupture 
                          ? 'error.main' 
                          : stock.est_stock_faible 
                            ? 'warning.main' 
                            : 'text.primary'
                      }
                    >
                      {stock.stock_actuel}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{stock.stock_minimum}</TableCell>
                  <TableCell>{getStockStatusChip(stock)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatPrix(stock)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Ajuster le stock">
                        <IconButton 
                          size="small" 
                          onClick={() => handleAjusterStock(stock)}
                          sx={{ 
                            bgcolor: 'primary.light', 
                            '&:hover': { bgcolor: 'primary.main', color: 'white' }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Transférer">
                        <IconButton 
                          size="small" 
                          onClick={() => handleTransfertStock(stock)}
                          sx={{ 
                            bgcolor: 'info.light', 
                            '&:hover': { bgcolor: 'info.main', color: 'white' }
                          }}
                        >
                          <SwapHoriz fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Voir mouvements">
                        <IconButton 
                          size="small" 
                          onClick={() => handleVoirMouvements(stock)}
                          sx={{ 
                            bgcolor: 'primary.main', 
                            '&:hover': { bgcolor: 'secondary.main', color: 'white' }
                          }}
                        >
                          <History fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} sur ${count}`
          }
        />
      </TableContainer>

      {/* ===== MODALS ===== */}
      <AjusterStockModal
        open={ajustementModalOpen}
        onClose={() => setAjustementModalOpen(false)}
        stock={selectedStock}
        succursales={succursales}
        onSuccess={handleAjustementSuccess}
      />

      <TransfertStockModal
        open={transfertModalOpen}
        onClose={() => setTransfertModalOpen(false)}
        stock={selectedStock}
        succursales={succursales}
        onSuccess={handleTransfertSuccess}
      />

      <MouvementsStockModal
        open={mouvementsModalOpen}
        onClose={() => setMouvementsModalOpen(false)}
        stock={selectedStock}
        succursales={succursales}
      />
    </Box>
  );
};

export default StockList;